import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  Send,
  CheckCircle,
  Calendar,
  MessageCircle,
} from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { addDoc, collection, Timestamp } from "firebase/firestore";
import { db } from "../firebase";


const ContactSection = () => {
  const navigate = useNavigate();
  const handleBookAppointment = () => navigate("/book-appointment");
  const goToBranches = () => {
  const section = document.getElementById("branches");
  if (section) {
    section.scrollIntoView({ behavior: "smooth" });
  }
};
  const [ariaMessage, setAriaMessage] = useState("");

  const [errors, setErrors] = useState({
  name: "",
  phone: "",
  email: "",
});

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
    preferredTime: "",
    urgency: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showChatbot, setShowChatbot] = useState(false);
  const { toast } = useToast();

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
  e.preventDefault();
  setIsSubmitting(true);

  try {
    // 🔐 Validate Name (letters and spaces only)
    if (!formData.name.trim()) {
  const msg = "Name is required.";
  setErrors((prev) => ({ ...prev, name: msg }));
  setAriaMessage(msg);
  setTimeout(() => setAriaMessage(""), 5000); 
  toast({ title: "Error", description: msg, variant: "destructive" });
  setIsSubmitting(false);
  return;
}

if (!/^[A-Za-z\s]+$/.test(formData.name)) {
  const msg = "Name must contain only letters and spaces.";
  setErrors((prev) => ({ ...prev, name: msg }));
  setAriaMessage(msg);
  toast({ title: "Invalid Name", description: msg, variant: "destructive" });
  setIsSubmitting(false);
  return;
}
// Reset error if valid
if (errors.name) setErrors((prev) => ({ ...prev, name: "" }));


    // 🔐 Validate Phone (10 digits only)
    if (!formData.phone.trim()) {
  const msg = "Phone is required.";
  setErrors((prev) => ({ ...prev, phone: msg }));
  setAriaMessage(msg);
  setTimeout(() => setAriaMessage(""), 5000); // Clears message after 5s
  toast({
    title: "Error",
    description: msg,
    variant: "destructive",
  });
  setIsSubmitting(false);
  return;
}

if (!/^\d{10}$/.test(formData.phone)) {
  const msg = "Phone number must be 10 digits.";
  setErrors((prev) => ({ ...prev, phone: msg }));
  setAriaMessage(msg);
  
  setIsSubmitting(false);
  return;
}

// Clear phone error if valid
setErrors((prev) => ({ ...prev, phone: "" }));

// Email validation example
if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
  const msg = "Enter a valid email address.";
  setErrors(prev => ({ ...prev, email: msg }));
  setAriaMessage(msg);
  toast({ title: "Invalid Email", description: msg, variant: "destructive" });
  setIsSubmitting(false);
  return;
}

setErrors((prev) => ({ ...prev, email: "" }));

// 🔐 Validate Service
if (!formData.service) {
  const msg = "Please select a service of interest.";
  setErrors((prev) => ({ ...prev, service: msg }));
  setAriaMessage(msg);
  setIsSubmitting(false);
  return;
} else {
  setErrors((prev) => ({ ...prev, service: "" }));
}

// 🔐 Validate Preferred Contact Time
if (!formData.preferredTime) {
  const msg = "Please select your preferred contact time.";
  setErrors((prev) => ({ ...prev, preferredTime: msg }));
  setAriaMessage(msg);
  setIsSubmitting(false);
  return;
} else {
  setErrors((prev) => ({ ...prev, preferredTime: "" }));
}



    // ✅ 1. Save to Firestore
    await addDoc(collection(db, "contacts"), {
      ...formData,
      createdAt: Timestamp.now(),
    });

    // ✅ 2. Send email to user + admin
    await fetch(
      "https://vpjc-api-805872224468.asia-south1.run.app/api/send-contact-email",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      }
    );

    // ✅ 3. Show success message
    const successMsg = "Message sent successfully! We will contact you within 24 hours.";
    setAriaMessage(successMsg);
    toast({
      title: "Message Sent Successfully!",
      description: "We will contact you within 24 hours.",
    });

    // ✅ 4. Reset form
    setFormData({
      name: "",
      email: "",
      phone: "",
      service: "",
      message: "",
      preferredTime: "",
      urgency: "",
    });
  } catch (error) {
    console.error("Error saving contact form data:", error);
    const msg = "Please try again later.";
    setAriaMessage(msg);
    toast({
      title: "Error",
      description: msg,
      variant: "destructive",
    });
  } finally {
    setIsSubmitting(false);
  }
};


  const contactInfo = [
    {
      icon: Phone,
      title: "Call Us",
      info: "+91 92810 10745",
      description: "Available 24/7 for emergencies",
    },
    {
      icon: Mail,
      title: "Email Us",
      info: "contact@jastiphysiochiro.com",
      description: "We'll respond at the earliest",
    },
    {
      icon: MapPin,
      title: "Visit Our Clinics",
      info: "2 Premium Locations",
      description: "Gajuwaka & Kurmannpalem branches",
    },
    {
      icon: Clock,
      title: "Operating Hours",
      info: [
        "Mon-Sat: 09:00 AM to 09:00 PM",
        "Sun: 09:00 AM to 05:00 PM",
      ],

      description: "Optimized hours to serve you better",
    },
  ];

  return (
    <section
      id="contact"
      className="py-20 bg-gradient-to-br from-slate-50 via-white to-blue-50 relative overflow-hidden"
    >
      <div className="absolute -top-24 -right-24 w-96 h-96 bg-[#204080]/5 rounded-full blur-3xl"></div>
      <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-[#2E5AAC]/5 rounded-full blur-3xl"></div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-3 bg-[#204080]/10 text-[#204080] px-5 py-3 rounded-full text-base font-semibold mb-6">
            <MessageCircle size={20} />
            <span>Contact Us</span>
          </div>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-[#204080] to-[#3a5bb8] drop-shadow-sm mb-4 tracking-tight leading-snug font-serif">
            Get in Touch Start Your Journey Today
            {/* <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#204080] to-[#2E5AAC]">
              Start Your Journey Today
            </span> */}
          </h2>

          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Ready to take the first step toward better health? Contact us to
            schedule your consultation or learn more about our services.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 mb-16">
          {/* Contact Form */}
          <Card className="p-8 bg-white/80 backdrop-blur-sm border-0 shadow-2xl">
            <h3 className="text-2xl font-bold text-[#204080] mb-6">
              Send us a Message
            </h3>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div aria-live="assertive" className="sr-only">
  {ariaMessage}
</div>

              <div className="grid md:grid-cols-2 gap-4">
                {/* Full Name Field */}
<div className="space-y-2">
  <label id="full-name-label" className="text-sm font-medium text-gray-700">
    Full Name *
  </label>
  <Input
    aria-labelledby="full-name-label"
    aria-invalid={errors.name ? "true" : "false"}
    aria-describedby={errors.name ? "name-error" : undefined}
    value={formData.name}
    onChange={(e) => {
      let value = e.target.value.replace(/[^A-Za-z\s]/g, "");
      value = value.replace(/\b\w/g, (char) => char.toUpperCase());
      handleInputChange("name", value);
    }}
    placeholder="Your full name"
    required
    className="border-gray-200 focus:border-[#204080] focus:ring-[#204080]/20"
  />
  {errors.name && (
    <div id="name-error" className="text-red-600 text-sm mt-1">
      {errors.name}
    </div>
  )}

</div>

                {/* Phone Number Field */}
<div className="space-y-2">
  <label id="phone-label" className="text-sm font-medium text-gray-700">
    Phone Number *
  </label>
  <Input
    aria-labelledby="phone-label"
    aria-invalid={errors.phone ? "true" : "false"}
    aria-describedby={errors.phone ? "phone-error" : undefined}
    type="tel"
    value={formData.phone}
    onChange={(e) => {
      const value = e.target.value.replace(/[^0-9]/g, "").slice(0, 10);
      handleInputChange("phone", value);
      if (errors.phone) setErrors((prev) => ({ ...prev, phone: "" })); // clear error on change
    }}
    placeholder="+91 92810 10745"
    maxLength={10}
    required
    className="border-gray-200 focus:border-[#204080] focus:ring-[#204080]/20"
  />
  {errors.phone && (
  <div id="phone-error" className="text-red-600 text-sm mt-1">
    {errors.phone}
  </div>
)}

</div>

              </div>

              <div className="space-y-2">
  <label id="email-label" className="text-sm font-medium text-gray-700">
    Email Address *
  </label>
  <Input
    aria-labelledby="email-label"
    aria-invalid={errors.email ? "true" : "false"}
    aria-describedby={errors.email ? "email-error" : undefined}
    type="email"
    value={formData.email}
    onChange={(e) => {
      handleInputChange("email", e.target.value);
      if (errors.email) setErrors((prev) => ({ ...prev, email: "" })); // clear error on change
    }}
    placeholder="your.email@example.com"
    required
    className="border-gray-200 focus:border-[#204080] focus:ring-[#204080]/20"
  />
  {errors.email && (
  <div id="email-error" className="text-red-600 text-sm mt-1">
    {errors.email}
  </div>
)}

</div>

              

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label id="service-label" className="text-sm font-medium text-gray-700">
                    Service of Interest *
                  </label>
                  <Select
                    labelId="service-label"
                    value={formData.service}
                    onValueChange={(value) =>
                      handleInputChange("service", value)
                    }
                  >
                    <SelectTrigger className="border-gray-200 focus:border-[#204080] focus:ring-[#204080]/20">
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Chiropractic Care">
                        Chiropractic Care
                      </SelectItem>
                      <SelectItem value="Pain Relief & Management">
                        Pain Relief & Management
                      </SelectItem>
                      <SelectItem value="Sports Injury Recovery">
                        Sports Injury Recovery
                      </SelectItem>
                      <SelectItem value="Rehabilitation">
                        Rehabilitation
                      </SelectItem>
                      <SelectItem value="Wellness Programs">
                        Wellness Programs
                      </SelectItem>
                      <SelectItem value="Corporate Wellness">
                        Corporate Wellness
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.service && (
  <div className="text-red-600 text-sm mt-1">{errors.service}</div>
)}

                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">
                    Preferred Contact *
                  </label>
                  <Select
                    value={formData.preferredTime}
                    onValueChange={(value) =>
                      handleInputChange("preferredTime", value)
                    }
                  >
                    <SelectTrigger className="border-gray-200 focus:border-[#204080] focus:ring-[#204080]/20">
                      <SelectValue placeholder="Select time" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Morning (8AM-12PM)">
                        Morning (8AM-12PM)
                      </SelectItem>
                      <SelectItem value="Afternoon (12PM-5PM)">
                        Afternoon (12PM-5PM)
                      </SelectItem>
                      <SelectItem value="Evening (5PM-8PM)">
                        Evening (5PM-8PM)
                      </SelectItem>
                      <SelectItem value="anytime">Anytime</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.preferredTime && (
  <div className="text-red-600 text-sm mt-1">{errors.preferredTime}</div>
)}
                </div>
                

              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Message
                </label>
                <Textarea
                  value={formData.message}
                  onChange={(e) => handleInputChange("message", e.target.value)}
                  placeholder="Tell us about your condition, goals, or any questions you have..."
                  rows={4}
                  className="border-gray-200 focus:border-[#204080] focus:ring-[#204080]/20"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Urgency Level
                </label>
                <Select
                  value={formData.urgency}
                  onValueChange={(value) => handleInputChange("urgency", value)}
                >
                  <SelectTrigger className="border-gray-200 focus:border-[#204080] focus:ring-[#204080]/20">
                    <SelectValue placeholder="How urgent is your need?" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Routine - Within a week">
                      Routine - Within a week
                    </SelectItem>
                    <SelectItem value="Soon - Within 2-3 days">
                      Soon - Within 2-3 days
                    </SelectItem>
                    <SelectItem value="Urgent - Same day">
                      Urgent - Same day
                    </SelectItem>
                    <SelectItem value="Emergency - Immediate">
                      Emergency - Immediate
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-[#204080] to-[#2E5AAC] hover:shadow-xl text-white py-3 text-lg font-semibold rounded-lg transition-all duration-300 transform hover:scale-105"
              >
                {isSubmitting ? (
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    <span>Sending...</span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <Send size={20} />
                    <span>Send Message</span>
                  </div>
                )}
              </Button>
            </form>
          </Card>

          {/* Contact Information */}

{/* Contact Information */}
<div className="space-y-8">
  <div className="grid gap-6">
    {contactInfo.map((info, index) => {
      const IconComponent = info.icon;

      // Determine click action for each card
      const handleCardClick = () => {
        if (info.title === "Call Us") {
          window.location.href = `tel:${info.info.replace(/\s/g, "")}`;
        } else if (info.title === "Email Us") {
          window.location.href = `mailto:${info.info}`;
        } else if (info.title === "Visit Our Clinics") {
          goToBranches();
        }
      };

      return (
        <Card
          key={index}
          className="group relative p-6 bg-white/80 backdrop-blur-sm border-0 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 select-text cursor-pointer"
          onClick={handleCardClick} // entire card clickable
        >
          <div className="flex items-center space-x-4 relative z-10">
            {/* Icon */}
            <div
              className="flex-shrink-0 p-3 bg-gradient-to-br from-[#204080] to-[#2E5AAC] rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-300"
            >
              <IconComponent size={24} className="text-white" />
            </div>

            {/* Info */}
            <div className="flex-1">
              <h4
                className="text-lg font-bold text-[#204080] group-hover:text-[#2E5AAC] transition-colors duration-300"
              >
                {info.title}
              </h4>

              <div className="text-gray-800 font-medium space-y-1">
                {Array.isArray(info.info) ? (
                  info.info.map((line, i) => (
                    <div key={i}>{line}</div>
                  ))
                ) : (
                  <span
                    className={`${
                      info.title === "Call Us" || info.title === "Email Us"
                        ? "text-[#204080] hover:underline"
                        : ""
                    }`}
                    onClick={(e) => {
                      e.stopPropagation(); // allow text copy
                      if (info.title === "Call Us") {
                        window.location.href = `tel:${info.info.replace(/\s/g, "")}`;
                      } else if (info.title === "Email Us") {
                        window.location.href = `mailto:${info.info}`;
                      } else if (info.title === "Visit Our Clinics") {
                        goToBranches();
                      }
                    }}
                  >
                    {info.info}
                  </span>
                )}
              </div>

              <p className="text-gray-600 text-sm">{info.description}</p>
            </div>
          </div>

          {/* Hover overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#204080]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg pointer-events-none"></div>
        </Card>
      );
    })}
</div>

            {/* Quick Actions */}
            <Card className="p-6 bg-gradient-to-r from-[#204080] to-[#2E5AAC] text-white border-0 shadow-2xl">
              <h3 className="text-xl font-bold mb-4">Quick Actions</h3>
              <div className="space-y-3">
                <button
                  className="w-full flex items-center justify-center space-x-2 bg-white/20 hover:bg-white/30 backdrop-blur-sm px-4 py-3 rounded-lg font-medium transition-all duration-300"
                  onClick={handleBookAppointment}
                >
                  <Calendar size={18} />
                  <span>Book Emergency Appointment</span>
                </button>
                <a
                  href="tel:+91 92810 10745"
                  className="w-full flex items-center justify-center space-x-2 bg-white/20 hover:bg-white/30 backdrop-blur-sm px-4 py-3 rounded-lg font-medium transition-all duration-300"
                  style={{ textDecoration: "none" }}
                >
                  <Phone size={18} />
                  <span>Call Now for Immediate Help</span>
                </a>
                <button
                  className="w-full flex items-center justify-center space-x-2 bg-white/20 hover:bg-white/30 backdrop-blur-sm px-4 py-3 rounded-lg font-medium transition-all duration-300"
                  onClick={() =>
                    window.open("https://wa.me/+919281010745", "_blank")
                  }
                >
                  <MessageCircle size={18} />
                  <span>Live Chat with Expert</span>
                </button>
              </div>
            </Card>

            {/* Trust Badges */}
            <Card className="p-6 bg-white/90 backdrop-blur-sm border border-gray-200 shadow-lg">
              <h4 className="text-lg font-bold text-[#204080] mb-4">
                Your Privacy is Protected
              </h4>
              <div className="space-y-3 text-sm text-gray-600">
                {/* <div className="flex items-center space-x-2">
                  <CheckCircle size={16} className="text-green-500" />
                  <span>HIPAA Compliant</span>
                </div> */}
                <div className="flex items-center space-x-2">
                  <CheckCircle size={16} className="text-green-500" />
                  <span>Secure Data Encryption</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle size={16} className="text-green-500" />
                  <span>No Information Sharing</span>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
